import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shortsAPI, ThumbnailJob, ThumbnailJobParams } from '../../services/api';

interface Props {
  /** A Short's clip id or an episode key (SxxEyy); null closes the dialog. */
  targetKey: string | null;
  /** A full episode's thumbnail pairs two people. */
  vod?: boolean;
  title?: string;
  onClose: () => void;
  onDone?: () => void;
}

const JOB_TEXT: Record<ThumbnailJob['status'], string> = {
  queued: 'Queued: waiting for REAPER to pick it up',
  rendering: 'REAPER is composing the thumbnails',
  done: 'Done: the new thumbnails are in the rotation',
  failed: 'REAPER could not render it',
};

/**
 * The face picker from REAPER's export window (portal §50.2), in the dashboard:
 * choose a face (a pair for a full episode) per rank, and REAPER re-renders
 * just those ranks. Ranks you don't touch keep their current image.
 */
const ThumbnailFacePicker: React.FC<Props> = ({ targetKey, vod = false, title, onClose, onDone }) => {
  const qc = useQueryClient();
  const perPick = vod ? 2 : 1;
  const [rank, setRank] = useState(1);
  const [picks, setPicks] = useState<Record<number, string[]>>({});
  const [captionFor, setCaptionFor] = useState<Record<number, number>>({});
  const [text, setText] = useState('');

  const opts = useQuery({
    queryKey: ['shorts', 'thumb-options', targetKey],
    queryFn: () => shortsAPI.getThumbnailOptions(targetKey as string),
    enabled: !!targetKey,
    // Follow the job once one is queued; REAPER composes in seconds.
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.latest_job?.status;
      return s === 'queued' || s === 'rendering' ? 5_000 : false;
    },
  });

  useEffect(() => {
    setPicks({});
    setCaptionFor({});
    setRank(1);
    setText('');
  }, [targetKey]);

  const job = opts.data?.latest_job;
  const prevStatus = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevStatus.current && prevStatus.current !== 'done' && job?.status === 'done') onDone?.();
    prevStatus.current = job?.status;
  }, [job?.status, onDone]);

  const ranks = useMemo(() => {
    const have = (opts.data?.candidates ?? []).map((c) => c.rank);
    return have.length ? have : [1, 2, 3];
  }, [opts.data]);
  const captions = (opts.data?.options?.captions as string[] | undefined) ?? [];
  const current = opts.data?.candidates.find((c) => c.rank === rank);

  const toggle = (faceId: string) => setPicks((cur) => {
    const mine = cur[rank] ?? [];
    if (mine.includes(faceId)) return { ...cur, [rank]: mine.filter((x) => x !== faceId) };
    const next = perPick === 1 ? [faceId] : [...mine, faceId].slice(-perPick);
    return { ...cur, [rank]: next };
  });

  const submit = useMutation({
    mutationFn: () => {
      const params: ThumbnailJobParams = {
        mode: vod ? 'vod' : captions.length ? 'both' : 'people',
        picks: Object.entries(picks)
          .filter(([, ids]) => ids.length)
          .map(([r, ids]) => ({
            rank: Number(r), face_ids: ids,
            ...(captionFor[Number(r)] != null ? { caption_index: captionFor[Number(r)] } : {}),
          })),
        ...(text.trim() ? { text: text.trim() } : {}),
      };
      return shortsAPI.createThumbnailJob(targetKey as string, params);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shorts', 'thumb-options', targetKey] }),
  });

  const pickedRanks = Object.entries(picks).filter(([, ids]) => ids.length).map(([r]) => r);
  const incomplete = Object.entries(picks).some(([, ids]) => ids.length && ids.length < perPick);
  const active = job && (job.status === 'queued' || job.status === 'rendering');
  const nothingScanned = opts.data && !opts.data.people.length;

  return (
    <Dialog open={!!targetKey} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Pick thumbnail faces{title ? ` · ${title}` : ''}</DialogTitle>
      <DialogContent dividers>
        {opts.isLoading ? <CircularProgress size={20} /> : opts.error ? (
          <Alert severity="error">{(opts.error as any)?.message}</Alert>
        ) : nothingScanned ? (
          <Alert severity="info">
            REAPER hasn't sent face options for this one yet. They come from its
            "Find faces" scan; once REAPER uploads them, they appear here to pick from.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {job && (
              <Alert severity={job.status === 'failed' ? 'error' : job.status === 'done' ? 'success' : 'info'}
                     icon={active ? <CircularProgress size={16} /> : undefined}>
                {JOB_TEXT[job.status]}
                {job.status === 'done' && job.result?.replaced?.length
                  ? ` (replaced #${job.result.replaced.join(', #')})` : ''}
                {job.error ? `: ${job.error}` : ''}
                {job.result?.warnings?.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {job.result.warnings.map((w) => <li key={w}>{w}</li>)}
                  </Box>
                ) : null}
              </Alert>
            )}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">Rank to change:</Typography>
              <ToggleButtonGroup size="small" exclusive value={rank}
                                 onChange={(_, v) => v != null && setRank(v)}>
                {ranks.map((r) => (
                  <ToggleButton key={r} value={r}>
                    #{r}{picks[r]?.length ? ' ✓' : ''}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                now: {current?.person || '—'}
                {current?.after_days != null ? ` · goes up on day ${current.after_days}` : ''}
              </Typography>
              {captions.length > 0 && (
                <Select size="small" displayEmpty value={captionFor[rank] ?? ''}
                        onChange={(e) => setCaptionFor((c) => ({ ...c, [rank]: Number(e.target.value) }))}>
                  <MenuItem value="" disabled>caption…</MenuItem>
                  {captions.map((c, i) => (
                    <MenuItem key={i} value={i + 1}>{i + 1}. {c}</MenuItem>
                  ))}
                </Select>
              )}
              {vod && (
                <TextField size="small" label="Thumbnail text (optional)" value={text}
                           onChange={(e) => setText(e.target.value)} sx={{ minWidth: 240 }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {vod ? 'Pick two people for this rank.' : 'Pick one face for this rank.'} Ranks
              you don't pick keep their current image.
            </Typography>
            {opts.data?.people.map((p) => (
              <Box key={p.person}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                  {p.person}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                  {p.faces.map((f) => {
                    const on = (picks[rank] ?? []).includes(f.face_id);
                    return (
                      <Box key={f.face_id} onClick={() => toggle(f.face_id)} sx={{
                        flex: '0 0 auto', width: 110, cursor: 'pointer', borderRadius: 1,
                        border: '3px solid', borderColor: on ? 'primary.main' : 'transparent',
                      }}>
                        {f.image_url ? (
                          <Box component="img" src={f.image_url} alt={p.person}
                               sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover',
                                     display: 'block', borderRadius: 0.5 }} />
                        ) : <Box sx={{ width: '100%', aspectRatio: '1 / 1', bgcolor: 'action.hover' }} />}
                        {f.at_seconds != null && (
                          <Typography variant="caption" sx={{ fontSize: 10 }} color="text.secondary">
                            {Math.floor(f.at_seconds / 60)}:{String(Math.floor(f.at_seconds % 60)).padStart(2, '0')}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
            {opts.data?.scanned_at && (
              <Typography variant="caption" color="text.secondary">
                Faces from REAPER's scan, {new Date(opts.data.scanned_at).toLocaleString()}.
              </Typography>
            )}
          </Stack>
        )}
        {submit.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(submit.error as any)?.response?.data?.detail || (submit.error as any)?.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {pickedRanks.length > 0 && (
          <Chip size="small" label={`re-render #${pickedRanks.join(', #')}`} sx={{ mr: 'auto', ml: 1 }} />
        )}
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained"
                disabled={!pickedRanks.length || incomplete || !!active || submit.isPending}
                onClick={() => submit.mutate()}>
          {submit.isPending ? 'Queueing…' : 'Re-render in REAPER'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThumbnailFacePicker;
